import { ColDef } from 'ag-grid-community';
import { useApi } from 'api/api-context';
import { translateRevisionPeriod } from 'components/inputs/revision-period-select/RevisionPeriodSelect';
import ActionsColumn from 'components/tables/data/revision-plan/columns/ActionsColumn';
import DeviceIdColumn from 'components/tables/data/revision-plan/columns/DeviceIdColumn';
import ManufacturerColumn from 'components/tables/data/revision-plan/columns/ManufacturerColumn';
import ManufacturedColumn from 'components/tables/data/revision-plan/columns/ManufacturedColumn';
import SerialNumberColumn from 'components/tables/data/revision-plan/columns/SerialNumberColumn';
import DeviceDeviceTypeColumn from 'components/tables/data/revision-plan/columns/DeviceDeviceTypeColumn';
import DeviceSubtypeColumn from 'components/tables/data/revision-plan/columns/DeviceSubtypeColumn';
import DescriptionColumn from 'components/tables/data/revision-plan/columns/DescriptionColumn';
import DeviceNameColumn from 'components/tables/data/revision-plan/columns/DeviceNameColumn';
import DeviceTypeColumn from 'components/tables/data/revision-plan/columns/DeviceTypeColumn';
import ExternalIdColumn from 'components/tables/data/revision-plan/columns/ExternalIdColumn';
import NextRevisionColumn from 'components/tables/data/revision-plan/columns/NextRevisionColumn';
import OrganizationNameColumn from 'components/tables/data/revision-plan/columns/OrganizationNameColumn';
import RevisionPlanIdColumn from 'components/tables/data/revision-plan/columns/RevisionPlanIdColumn';
import TypeColumn from 'components/tables/data/revision-plan/columns/TypeColumn';
import RemoteSourceSelectFilter from 'components/tables/filters/RemoteSourceSelectFilter';
import RemoteSourceColumn from 'components/tables/data/revision-plan/columns/RemoteSourceColumn';
import DataTable, { IDataTablePublic } from 'components/tables/DataTable';
import DateFilter, { DateColumnFilter } from 'components/tables/filters/DateFilter';
import useObjectColumn from 'components/tables/hierarchy/ObjectColumn';
import DeviceSelectFilter from 'components/tables/filters/DeviceSelectFilter';
import DeviceTypeSelectFilter from 'components/tables/filters/DeviceTypeSelectFilter';
import DeviceSubtypeSelectFilter from 'components/tables/filters/device-subtype/DeviceSubtypeSelectFilter';
import OrganizationMultiSelectFilter, {
  OrganizationMultiColumnFilter,
} from 'components/tables/filters/OrganizationMultiSelectFilter';
import { forwardRef, useCallback, useMemo } from 'react';
import { FEATURE_TOGGLE_REMOTE_SOURCES } from 'env';
import { getResponsiveActionsColumnWidth, useResponsiveDesign } from 'hooks/use-responsive-design';

interface RevisionPlanTableProps {
  suppressSaveProfiles?: boolean;
  suppressProfileChangedNotification?: boolean;
  initialFilters?: Record<string, any>;
  afterTitle?: React.ReactNode;
}

/**
 * Table for displaying revision plans.
 */
const RevisionPlanTable = forwardRef<IDataTablePublic, RevisionPlanTableProps>(
  ({ afterTitle, initialFilters, suppressProfileChangedNotification, suppressSaveProfiles }, ref) => {
    const { getAction } = useApi();
    const { tableActionMode } = useResponsiveDesign();
    const objectColumn = useObjectColumn({
      breadcrumbField: 'department.breadcrumbs',
      organizationFilterKey: 'organization.organizationId',
      permissionSlug: 'browse-devices',
      legacyColumnIds: ['department.departmentName'],
    });

    const action = useCallback(async () => {
      const action = getAction('DeviceListRevisionPlan');

      const revisionPlans = await action();

      return revisionPlans.flatMap((rp) => {
        const row = {
          ...rp,
          deviceId: rp.device.deviceId,
          deviceName: rp.device.deviceName,
          manufacturer: rp.device.manufacturer,
          serialNumber: rp.device.serialNumber,
          manufactured: rp.device.manufactured,
          _revisionPeriodPretty: translateRevisionPeriod(rp.revisionPeriod),
        };

        const childRows = (rp.device.childDevices ?? []).map((childDevice) => {
          const parentDevice = {
            deviceId: rp.device.deviceId,
            deviceName: rp.device.deviceName,
          };

          return {
            ...row,
            device: { ...childDevice, parentDevice },
            deviceId: childDevice.deviceId,
            deviceName: childDevice.deviceName,
            parentDevice,
            isInheritedFromParent: true,
            inheritedFromDeviceName: rp.device.deviceName,
          };
        });

        return [row, ...childRows];
      });
    }, [getAction]);

    const columns = useMemo<ColDef[]>(
      () => [
        {
          field: 'revisionPlanId',
          headerName: 'ID',
          sortable: true,
          unSortIcon: true,
          width: 76,
          minWidth: 76,
          maxWidth: 76,
          resizable: false,
          cellRenderer: RevisionPlanIdColumn,
        },
        {
          field: 'type',
          headerName: 'Úkon',
          minWidth: 320,
          filter: true,
          sortable: true,
          unSortIcon: true,
          cellRenderer: TypeColumn,
        },
        {
          field: 'description',
          headerName: 'Popis',
          minWidth: 200,
          filter: true,
          cellRenderer: DescriptionColumn,
        },
        {
          field: 'nextRevision',
          headerName: 'Najbližšia revízia',
          minWidth: 300,
          filter: DateColumnFilter,
          floatingFilter: true,
          floatingFilterComponent: DateFilter.configure({ filterKey: 'nextRevision' }),
          sortable: true,
          unSortIcon: true,
          cellRenderer: NextRevisionColumn,
        },
        {
          field: 'lastRevision',
          headerName: 'Posledná revízia',
          hide: true,
        },
        {
          field: '_revisionPeriodPretty',
          headerName: 'Perióda',
          hide: true,
        },
        {
          field: 'deviceId',
          headerName: 'ID zariadenia',
          sortable: true,
          unSortIcon: true,
          width: 140,
          minWidth: 140,
          filter: true,
          cellRenderer: DeviceIdColumn,
        },
        {
          field: 'deviceName',
          headerName: 'Názov zariadenia',
          minWidth: 300,
          filter: true,
          floatingFilter: true,
          floatingFilterComponent: DeviceSelectFilter,
          sortable: true,
          unSortIcon: true,
          cellRenderer: DeviceNameColumn,
        },
        {
          field: 'device.manufacturer',
          headerName: 'Výrobca',
          minWidth: 160,
          filter: true,
          sortable: true,
          unSortIcon: true,
          cellRenderer: ManufacturerColumn,
        },
        {
          field: 'device.serialNumber',
          headerName: 'Výrobné číslo',
          minWidth: 160,
          filter: true,
          sortable: true,
          unSortIcon: true,
          cellRenderer: SerialNumberColumn,
        },
        {
          field: 'device.manufactured',
          headerName: 'Rok výroby',
          minWidth: 140,
          filter: true,
          sortable: true,
          unSortIcon: true,
          cellRenderer: ManufacturedColumn,
        },
        {
          field: 'device.deviceType.deviceTypeId',
          headerName: 'ID zariadenia (typ)',
          hide: true,
          filter: true,
        },
        {
          field: 'device.deviceType.deviceTypeName',
          headerName: 'Zariadenie',
          minWidth: 220,
          filter: true,
          floatingFilter: true,
          floatingFilterComponent: DeviceTypeSelectFilter.configure({ filterKey: 'device.deviceType.deviceTypeId' }),
          sortable: true,
          unSortIcon: true,
          cellRenderer: DeviceDeviceTypeColumn,
        },
        {
          field: 'device.deviceSubtype.deviceTypeId',
          headerName: 'ID typu zariadenia',
          hide: true,
          filter: true,
        },
        {
          field: 'device.deviceSubtype.deviceTypeName',
          headerName: 'Typ zariadenia',
          minWidth: 220,
          filter: true,
          floatingFilter: true,
          floatingFilterComponent: DeviceSubtypeSelectFilter.configure({
            filterKey: 'device.deviceSubtype.deviceTypeId',
            type: 'equals',
          }),
          sortable: true,
          unSortIcon: true,
          cellRenderer: DeviceSubtypeColumn,
        },
        {
          field: 'organization.organizationId',
          headerName: 'ID organizácie',
          hide: true,
          filter: OrganizationMultiColumnFilter,
        },
        {
          field: 'organization.organizationName',
          headerName: 'Organizácia',
          minWidth: 275,
          filter: true,
          floatingFilter: true,
          floatingFilterComponent: OrganizationMultiSelectFilter.configure({ permissionSlug: 'browse-devices' }),
          sortable: true,
          unSortIcon: true,
          cellRenderer: OrganizationNameColumn,
        },
        ...objectColumn.columns,
        {
          field: 'deviceType.deviceTypeId',
          hide: true,
          filter: true,
        },
        {
          field: 'deviceType.deviceTypeName',
          headerName: 'Zariadenie (termín)',
          minWidth: 220,
          filter: true,
          floatingFilter: true,
          floatingFilterComponent: DeviceTypeSelectFilter,
          sortable: true,
          unSortIcon: true,
          cellRenderer: DeviceTypeColumn,
        },
        { field: 'device.building', headerName: 'Budova', hide: true },
        { field: 'device.floor', headerName: 'Poschodie', hide: true },
        { field: 'device.room', headerName: 'Miestnosť', hide: true },
        { field: 'device.longitude', headerName: 'Zemepisná dĺžka', hide: true },
        { field: 'device.latitude', headerName: 'Zemepisná šírka', hide: true },
        { field: 'device.responsiblePerson.fullName', headerName: 'Zodpovedná osoba', hide: true },
        ...(FEATURE_TOGGLE_REMOTE_SOURCES
          ? [
              {
                field: 'remoteSource.remoteSourceId',
                headerName: 'ID externého zdroja',
                hide: true,
                filter: true,
              },
              {
                field: 'remoteSource.name',
                headerName: 'Externý zdroj',
                minWidth: 200,
                filter: true,
                floatingFilter: true,
                floatingFilterComponent: RemoteSourceSelectFilter,
                sortable: true,
                unSortIcon: true,
                cellRenderer: RemoteSourceColumn,
              },
            ]
          : []),
        {
          field: 'externalId',
          headerName: 'Externé ID',
          minWidth: 160,
          cellRenderer: ExternalIdColumn,
        },
        {
          field: 'importId',
          headerName: 'ID importu',
          filter: true,
          hide: true,
        },
        {
          field: '_actions',
          headerName: '',
          pinned: 'right',
          width: getResponsiveActionsColumnWidth(192, tableActionMode),
          minWidth: getResponsiveActionsColumnWidth(192, tableActionMode),
          maxWidth: getResponsiveActionsColumnWidth(192, tableActionMode),
          cellRenderer: ActionsColumn,
        },
      ],
      [objectColumn.columns, tableActionMode]
    );

    return (
      <DataTable
        ref={ref}
        tableId="revision-plans"
        title="Sledovanie termínov"
        hideToggleDiscarded
        columns={columns}
        action={action}
        initialFilters={initialFilters}
        afterTitle={afterTitle}
        suppressProfileChangedNotification={suppressProfileChangedNotification}
        suppressSaveProfiles={suppressSaveProfiles}
        profileColumnAliases={objectColumn.profileColumnAliases}
        profileMigrationReady={objectColumn.profileMigrationReady}
        dataExport={{
          modalTitle: 'Exportovať sledovanie termínov',
          fileName: 'sledovanie-termínov.xlsx',
          columnKeys: [
            'revisionPlanId',
            'type',
            'description',
            'nextRevision',
            'lastRevision',
            '_revisionPeriodPretty',
            'organization.organizationId',
            'organization.organizationName',
            ...objectColumn.columnKeys,
            'deviceId',
            'deviceName',
            'device.manufacturer',
            'device.serialNumber',
            'device.manufactured',
            'device.deviceType.deviceTypeName',
            'device.deviceSubtype.deviceTypeName',
            'deviceType.deviceTypeName',
            'device.building',
            'device.room',
            'device.floor',
            'device.longitude',
            'device.latitude',
            'device.responsiblePerson.fullName',
            'remoteSource.remoteSourceId',
            'remoteSource.name',
            'externalId',
          ],
          columnReplacements: objectColumn.columnReplacements,
          ready: objectColumn.exportReady,
          error: objectColumn.exportError,
        }}
      />
    );
  }
);

export default RevisionPlanTable;
